import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Column } from "../../common/components/ColumnsSelector/ColumnsSelector";
import { ColumnIds } from "../pricing_list/hooks/columns/common";
import { borderGrey } from "../../core/theme";
import {
	Box,
	Alert,
	LinearProgress,
	TableHead,
	TableBody,
	Table,
	TableContainer,
	TableRow,
} from "@mui/material";
import { If } from "../../common/components/If";
import { useAppDispatch, useAppSelector } from "../../common/hooks/default";
import {
	selectPricingsGroupCount,
	selectPricingsGroupCountIsLoading,
	selectPricingsGroupCountError,
} from "../grouping/grouping.selector";
import { useReloadGroupCountOnPricingWs } from "./hooks/useReloadGroupCountOnPricingWs";
import { useInitialGroupLoading } from "./hooks/useInitialGroupLoading";
import { TableVirtuoso } from "react-virtuoso";
import { GroupedPricingListRow } from "./GroupedPricingListRow";
import { expandGroup, selectGroup, reset } from "../grouping/grouping.slice";
import {
	groupRowKey,
	isGroup,
	isPricing,
	isPricingRun,
	PricingListItem,
} from "./groupUtils";
import { loadGroupPricings } from "../grouping/grouping.thunk";
import { PricingTableHeader } from "./rows/PricingTableHeader";
import { useReloadGroups } from "./hooks/useReloadGroups";
import { useMergePricingsIntoGroups } from "./hooks/useMergePricingsIntoGroups";
import {
	Action,
	CurtailmentOutput,
	Pricing,
} from "../pricing_list/pricingListSlice";
import GraphModal from "../pricing_list/components/actionModal/GraphModal";
import { ReadCommentModal } from "../pricing_list/components/ReadCommentModal";
import { useForceReloadOnTenderChanges } from "./hooks/useForceReloadOnTenderChanges";
import { GeneratingCurtailmentObjectFailedModal } from "../../common/BuildCurtailmentObjectsComponents/GeneratingCurtailmentObjectFailedModal";

export interface PricingGroupTableGroupsProps {
	tenderId?: number;
	odataQuery?: string;
	selectedColumns: Column<ColumnIds>[];
	clearSelection: () => void;
	availableActions: Action[];
	selectAll: () => void;
	actOnPricings: (action: Action) => void;
	onDownloadSelectedRunsToExcel: () => void;
	onPricingsDownload: () => void;
	onDownloadRunToExcelByRunIds: (ids: number[]) => void;
	onDownloadPbParams: (pricingRunId: number) => void;
	onDownloadJson: (id: number) => void;
	onDownloadBookingJson: (id: number) => void;
	actionModal: React.ReactNode;
	data: {
		node: PricingListItem;
		depth: number;
		parents: PricingListItem[];
	}[];
	onHide: (id: number) => void;
	showHidden: boolean;
	thereAreHiddenRows: boolean;
}

const styles = {
	wrapper: {
		display: "flex",
		flexDirection: "column",
		width: "100%",
		marginTop: "40px",
		borderTop: `1px solid ${borderGrey}`,
		height: "100%",
		overflowY: "scroll",
	},
	fixed: {
		tableLayout: "fixed",
	},
};

export const Row: any = React.forwardRef(function CustomTableRow(
	props: any,
	ref: any
) {
	const isSelected = useMemo(() => {
		if (!isPricing(props.item.node)) {
			return false;
		}
		const parentSelected = props.item.parents.reduce(
			(acc: boolean, curr: any) => acc || curr.isSelected,
			false
		);
		if (parentSelected) {
			return true;
		}
		return props.item.node.isSelected;
	}, [props.item]);

	return (
		<TableRow
			{...props}
			ref={ref}
			sx={{
				...(props.sx || {}),
				...(isSelected
					? {
							background: "#D1DFF9!important",
							td: { background: "#D1DFF9!important" },
					  }
					: {
							"&:hover": {
								background: "#ccd4da!important",
								td: { background: "#ccd4da!important" },
							},
					  }),
			}}
			data-testid={isSelected ? "selected" : "notselected"}
		>
			{props.children}
		</TableRow>
	);
});

const TbComponents = {
	Scroller: React.forwardRef(function TScroller(props: any, ref: any) {
		return <TableContainer {...props} ref={ref} />;
	}),
	Table: (props: any) => (
		<Table
			{...props}
			style={{ borderCollapse: "separate", width: "100%" }}
		/>
	),
	TableHead: TableHead,
	TableRow: Row,
	TableBody: React.forwardRef(function TBody(props: any, ref) {
		return <TableBody {...props} ref={ref} />;
	}),
};

export function PricingGroupTable(props: PricingGroupTableGroupsProps) {
	const {
		tenderId,
		clearSelection,
		odataQuery,
		availableActions,
		selectAll,
		actOnPricings,
		onDownloadSelectedRunsToExcel,
		onPricingsDownload,
		onDownloadRunToExcelByRunIds,
		onDownloadPbParams,
		onDownloadJson,
		onDownloadBookingJson,
		actionModal,
	} = props;

	const dispatch = useAppDispatch();
	const [selectedPricing, setSelectedPricing] = useState<Pricing | undefined>(
		undefined
	);
	const [comment, setComment] = useState<string | undefined>(undefined);
	const [curtailmentOutput, setCurtailmentOutput] =
		useState<CurtailmentOutput | null>(null);
	const pricingsGroupsCount = useAppSelector(selectPricingsGroupCount);
	const groupCountIsLoading = useAppSelector(
		selectPricingsGroupCountIsLoading
	);
	const groupCountError = useAppSelector(selectPricingsGroupCountError);

	useInitialGroupLoading(tenderId, odataQuery);
	useReloadGroups(tenderId, odataQuery);
	useReloadGroupCountOnPricingWs(tenderId, odataQuery);
	useForceReloadOnTenderChanges(tenderId, odataQuery);
	useMergePricingsIntoGroups(tenderId);

	const onGroupSelection = useCallback(
		(groupPath: string[]) => {
			dispatch(selectGroup({ groupPath }));
		},
		[dispatch]
	);

	const onLoadPricings = useCallback(
		(groupPath: string[]) => {
			if (odataQuery !== "N/A") {
				dispatch(loadGroupPricings(tenderId, odataQuery, groupPath));
			}
		},
		[dispatch, odataQuery]
	);

	const onGroupExpand = useCallback(
		(groupPath: string[]) => {
			dispatch(expandGroup({ groupPath }));
		},
		[dispatch]
	);

	const openGraphModal = (pricing: Pricing | undefined) => {
		setSelectedPricing(pricing);
	};

	const displayComment = useCallback(
		(status_comment: string) => {
			setComment(status_comment);
		},
		[setComment]
	);

	type CurtailmentErrorEntry = string | number | boolean | null | object;
	type CurtailmentErrors =
		| CurtailmentErrorEntry
		| CurtailmentErrorEntry[]
		| undefined;
	type CurtailmentPayload = {
		errors?: CurtailmentErrors;
		Error?: CurtailmentErrors;
		success?: CurtailmentOutput["success"];
	};

	const normalizeErrors = (rawErrors: CurtailmentErrors): string[] => {
		if (rawErrors == null) {
			return [];
		}

		if (typeof rawErrors === "string") {
			return [rawErrors];
		}

		if (Array.isArray(rawErrors)) {
			return rawErrors.map((entry) =>
				typeof entry === "string" ? entry : JSON.stringify(entry)
			);
		}

		return [JSON.stringify(rawErrors)];
	};

	const displayCurtailmentFailure = useCallback(
		(output: string | undefined) => {
			if (!output) {
				setComment("");
				return;
			}

			if (typeof output === "string") {
				setComment(output);
				return;
			}

			const normalizedOutput = output as CurtailmentPayload;
			const rawErrors = normalizedOutput.errors ?? normalizedOutput.Error;
			const errors = normalizeErrors(rawErrors);
			const success = Array.isArray(normalizedOutput.success)
				? normalizedOutput.success
				: [];

			setCurtailmentOutput({
				errors,
				success,
			});
		},
		[setCurtailmentOutput]
	);

	useEffect(() => {
		return () => {
			dispatch(reset());
		};
	}, []);

	const currency = useMemo(
		() =>
			props.data
				.filter((node) => isPricing(node.node))
				.map(({ node }) => (node as Pricing).currency)
				.reduce((prev, curr) => curr ?? prev, undefined),
		[props.data]
	);

	return (
		<Box sx={styles.wrapper}>
			<If condition={groupCountError}>
				<Alert severity="error">
					{"" + JSON.stringify(groupCountError)}
				</Alert>
			</If>
			<If condition={groupCountIsLoading}>
				<LinearProgress data-test-id="pricing-list-loading" />
			</If>
			<If condition={props.thereAreHiddenRows}>
				<Alert severity="info">Some rows are hidden</Alert>
			</If>
			<If
				condition={
					!groupCountIsLoading &&
					!pricingsGroupsCount?.length &&
					odataQuery !== "N/A"
				}
			>
				<Alert severity="warning">No items found</Alert>
			</If>
			<TableVirtuoso
				components={TbComponents as any}
				fixedHeaderContent={() => (
					<PricingTableHeader
						showHidden={props.showHidden}
						selectedColumns={props.selectedColumns}
						clearPricingSelection={clearSelection}
						availableActionsForSelection={availableActions}
						selectAll={selectAll}
						onAction={actOnPricings}
						onDownloadRunToExcel={onDownloadSelectedRunsToExcel}
						onPricingsDownload={onPricingsDownload}
						currency={currency}
					/>
				)}
				data={props.data}
				computeItemKey={(index, item) =>
					groupRowKey(item.node, item.parents)
				}
				itemContent={(index, item) => (
					<GroupedPricingListRow
						onHide={props.onHide}
						odataQuery={odataQuery}
						group={isGroup(item.node) ? item.node : undefined}
						pricing={isPricing(item.node) ? item.node : undefined}
						pricingRun={
							isPricingRun(item.node) ? item.node : undefined
						}
						depth={item.depth}
						parents={item.parents}
						onGroupSelection={onGroupSelection}
						onGroupExpand={onGroupExpand}
						onLoadPricings={onLoadPricings}
						selectedColumns={props.selectedColumns}
						onDisplayComment={displayComment}
						onDisplayCurtailmentFailure={displayCurtailmentFailure}
						onSubmit={function (id: number, action: Action): void {
							actOnPricings(action);
						}}
						onDownloadRunToExcel={onDownloadRunToExcelByRunIds}
						onDownloadPbParams={onDownloadPbParams}
						onDownloadJson={onDownloadJson}
						onDownloadBookingJson={onDownloadBookingJson}
						openGraphModal={openGraphModal}
						openWarningModal={displayComment}
						showHidden={props.showHidden}
					/>
				)}
			/>
			{actionModal}
			<If condition={!!selectedPricing}>
				<GraphModal
					open={!!selectedPricing}
					onClose={() => setSelectedPricing(undefined)}
					pricing={selectedPricing}
				/>
			</If>
			<ReadCommentModal
				isOpen={!!comment}
				comment={comment}
				onClose={() => setComment(undefined)}
			/>
			<GeneratingCurtailmentObjectFailedModal
				isOpen={!!curtailmentOutput}
				errors={curtailmentOutput?.errors || []}
				success={curtailmentOutput?.success || []}
				onClose={() => setCurtailmentOutput(null)}
			/>
		</Box>
	);
}
